import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, CircularProgress } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import SnackbarAlert from '../components/SnackbarAlert';
import PanelistInterviewsView from '../components/panels/PanelistInterviewsView';
import PanelManagementView from '../components/panels/PanelManagementView';
import dayjs from 'dayjs';
import { COLORS } from '../config/colors';

const PanelPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  
  // Check if user is HR or Admin
  const isHROrAdmin = user?.role === 'hr' || user?.role === 'admin';
  const PANELIST_EMAIL = user?.email || null;
  const PANELIST_NAME = user?.username || 'Unknown';

  useEffect(() => {
    if (!PANELIST_EMAIL && !isHROrAdmin) {
      setSnackbar({
        open: true,
        message: 'Email address not found. Backend needs to include email in JWT token.',
        severity: 'error'
      });
    }
  }, [PANELIST_EMAIL]);

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 4,
        py: 3,
        bgcolor: '#fff',
        borderBottom: '1px solid #e7e7e7',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700,color: COLORS.PRIMARY_BLUE_DARK, mb: 0.5 }}>
            Welcome, {PANELIST_NAME}
          </Typography>
          <Typography variant="body2" sx={{color: COLORS.PRIMARY_BLUE_DARK,}}>
            {isHROrAdmin ? 'Panel Management Dashboard' : 'Interview Panel Dashboard'} • {dayjs().format('MMMM D, YYYY')}
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', py: 4, px: 3 }}>
        {/* Only show tabs for HR/Admin users */}
        {isHROrAdmin && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
            >
              <Tab label="Panel Management" sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.PRIMARY_BLUE_DARK, }} />
              <Tab label="All Panelists" sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.PRIMARY_BLUE_DARK, }} />
            </Tabs>
          </Box>
        )}

        {/* Tab Content */}
        {!isHROrAdmin ? (
          // For panelists, directly show their interview view (no extra tabs)
          <PanelistInterviewsView 
            panelistEmail={PANELIST_EMAIL}
            panelistName={PANELIST_NAME}
            onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
            onSuccess={(message) => setSnackbar({ open: true, message, severity: 'success' })}
          />
        ) : (
          // For HR/Admin, show panel management with tabs
          <PanelManagementView 
            activeTab={activeTab}
            onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
            onSuccess={(message) => setSnackbar({ open: true, message, severity: 'success' })}
          />
        )}

        {/* Snackbar */}
        <SnackbarAlert snackbar={snackbar} setSnackbar={setSnackbar} />
      </Box>
    </Box>
  );
};

export default PanelPage;